import React, { useState, useRef } from "react";
import "../style/Toolbar.css";

const Toolbar = ({
  activeTab,
  onTabChange,
  showSuccessful,
  showFailed,
  onToggleSuccessful,
  onToggleFailed,
  onFiltersClick,
  currentPage,
  totalPageCount,
  handlePageChange,
  onKeywordSearch,
  searchKeyword,
  // SSE cluster props
  liveCount,
  connUI,
  connStatus,
  reconnectSSE,
}) => {
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPageCount;
  const debounceRef = useRef(null);
  const [localValue, setLocalValue] = useState(searchKeyword ?? "");

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setLocalValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onKeywordSearch?.(val);
    }, 400);
  };

  const handleClear = () => {
    setLocalValue("");
    onKeywordSearch?.("");
  };

  return (
    <div className="toolbar c-content-width">
      {" "}
      {/* ── Left: tabs ── */}
      <div className="toolbar-tabs">
        <button
          className={`tab-pill${activeTab === "section" ? " tab-pill--active" : ""}`}
          onClick={() => onTabChange("section")}
        >
          Section
        </button>
        <button
          className={`tab-pill${activeTab === "sentiment" ? " tab-pill--active" : ""}`}
          onClick={() => onTabChange("sentiment")}
        >
          Sentiment
        </button>
      </div>
      {/* ── Right: search + status toggles + pagination + filters + SSE ── */}
      <div className="toolbar-right">
        {/* Search bar */}
        <div className="toolbar-search">
          <svg className="toolbar-search__icon" viewBox="0 0 24 24" fill="none">
            <circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="16.5"
              y1="16.5"
              x2="21"
              y2="21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            className="toolbar-search__input"
            type="text"
            placeholder="Search by keyword…"
            value={localValue}
            onChange={handleSearchChange}
          />
          {localValue && (
            <button
              className="toolbar-search__clear"
              onClick={handleClear}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="toolbar-divider" />

        {/* Status toggles */}
        <div className="status-toggles">
          <button
            className={`status-toggle status-toggle--success${showSuccessful ? " active" : ""}`}
            onClick={onToggleSuccessful}
          >
            <span className="dot" />
            Successful
          </button>
          <button
            className={`status-toggle status-toggle--failed${showFailed ? " active" : ""}`}
            onClick={onToggleFailed}
          >
            <span className="dot" />
            Failed
          </button>
        </div>

        <div className="toolbar-divider" />

        {/* Pagination */}
        {totalPageCount > 1 && (
          <>
            <div className="toolbar-pagination">
              <button
                className="page-arrow"
                onClick={() => canPrev && handlePageChange(currentPage - 1)}
                disabled={!canPrev}
                aria-label="Previous page"
              >
                ‹
              </button>
              <span className="page-indicator">
                <span className="page-current">{currentPage}</span>
                <span className="page-sep">/</span>
                <span className="page-total">{totalPageCount}</span>
              </span>
              <button
                className="page-arrow"
                onClick={() => canNext && handlePageChange(currentPage + 1)}
                disabled={!canNext}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
            <div className="toolbar-divider" />
          </>
        )}

        {/* Filters */}
        <button className="filters-btn" onClick={onFiltersClick}>
          <svg viewBox="0 0 24 24">
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="11" y1="18" x2="13" y2="18" />
          </svg>
          Filters
        </button>

        {/* SSE status cluster */}
        {connUI && (
          <>
            <div className="toolbar-divider" />
            <div className="toolbar-sse-cluster">
              {liveCount > 0 && (
                <span className="sse-live-badge">+{liveCount} live</span>
              )}
              <div
                className="sse-conn-badge"
                style={{
                  background: connUI.bg,
                  borderColor: connUI.border,
                }}
              >
                <span
                  className={`sse-dot${connUI.pulse ? " sse-dot--pulse" : ""}`}
                  style={{ background: connUI.dot }}
                />
                <span className="sse-conn-label" style={{ color: connUI.text }}>
                  {connUI.label}
                </span>
              </div>
              {connStatus === "error" && (
                <button className="sse-reconnect-btn" onClick={reconnectSSE}>
                  Reconnect
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Toolbar;
